import React, {
  FC,
  ReactNode,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
  KeyboardEvent,
  FocusEvent,
} from 'react'
import classnames from 'classnames/bind'
import type { AutomationTarget } from '../../../types'
import Icon from '../icon'
import PopupTooltip from '../../overlays/popupTooltip'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable="true"], [tabindex]:not([tabindex="-1"]), [role="combobox"], [role="listbox"]'

export interface InlineInputProps extends AutomationTarget {
  /** Rendered when NOT editing (e.g. a <Text>). */
  display: ReactNode
  /** Rendered when editing (e.g. an <input>). Its first focusable child is focused on entry. */
  edit: ReactNode
  /** Controlled editing state. Omit for uncontrolled (focus in → edit, blur out → display). */
  isEditing?: boolean
  /** Fires whenever editing state should change. Required to observe changes in controlled mode. */
  onEditingChange?: (next: boolean) => void
  /**
   * How the container sizes across modes. Default 'content'.
   * - 'content': hugs the wider of the two slots (max-content), capped at 100%
   *   of parent. Zero shift on toggle. Good default for composite controls
   *   (e.g. a dropdown) since the box grows to fit the control.
   * - 'fill': width 100% of parent; both slots stretch.
   * - 'display': no dedicated behavior — currently fills the parent like 'fill'
   *   (the earlier display-width lock was reverted). Use 'content' to hug the
   *   value.
   */
  sizing?: 'content' | 'fill' | 'display'
  /** Editable affordance on the display slot. Default 'border'. */
  hoverAffordance?: 'border' | 'none'
  /** FontAwesome (v6) icon name shown right-aligned on hover (e.g. 'pencil', 'pen-to-square'). */
  editIcon?: string
  /** Auto-focus the first focusable element in the edit slot on entry. Default true. */
  autoFocusEdit?: boolean
  /**
   * Selector for the element to auto-focus on entry. Defaults to a broad
   * focusable selector covering native inputs and composite controls (via
   * [tabindex] / combobox / listbox). Override to target a specific control.
   */
  editFocusSelector?: string
  /** Disable editing entirely (display only). */
  disabled?: boolean
  /**
   * Tooltip content shown on hover when `disabled` is true.
   * Use to explain why the field is not editable (e.g. a business rule).
   * Only rendered when `disabled` is also true; ignored otherwise.
   */
  disabledTooltip?: ReactNode
  /** Root container class (placement, width, margin, overflow). */
  className?: string
  /** Root container inline style (e.g. maxWidth, width, margin). */
  style?: React.CSSProperties
  /** Inner wrapper classes for box-model alignment. */
  classNames?: { display?: string; edit?: string }
}

const InlineInput: FC<InlineInputProps> = ({
  display,
  edit,
  isEditing: controlledEditing,
  onEditingChange,
  sizing = 'content',
  hoverAffordance = 'border',
  editIcon,
  autoFocusEdit = true,
  editFocusSelector,
  disabled = false,
  disabledTooltip,
  className,
  style,
  classNames,
  elmId,
}) => {
  const [internalEditing, setInternalEditing] = useState(false)
  const isControlled = controlledEditing !== undefined
  const editing =
    (isControlled ? controlledEditing : internalEditing) && !disabled

  const editRef = useRef<HTMLDivElement>(null)

  const setEditing = useCallback(
    (next: boolean) => {
      if (disabled) return
      if (!isControlled) setInternalEditing(next)
      onEditingChange?.(next)
    },
    [disabled, isControlled, onEditingChange]
  )

  // Both slots stay mounted (no-shift sizing), so focus the edit slot's first
  // focusable imperatively on entry — mount-time autoFocus can't fire on toggle.
  // FUTURE: an onEdit (display→edit) callback would let consumers imperatively
  // open a focus-driven control (e.g. a dropdown) on entry — deliberately not
  // done here to avoid fighting the control's own focus/open/close handling.
  useLayoutEffect(() => {
    if (!editing || !autoFocusEdit) return
    const el = editRef.current?.querySelector<HTMLElement>(
      editFocusSelector ?? EDITABLE_SELECTOR
    )
    el?.focus()
  }, [editing, autoFocusEdit, editFocusSelector])

  const handleDisplayKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setEditing(true)
    }
  }

  const handleEditKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setEditing(false)
    }
  }

  // Exit only when focus leaves the edit wrapper, so composite edit slots
  // (input + buttons) can keep focus internally without exiting.
  const handleEditBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setEditing(false)
    }
  }

  // `inert` isn't typed on JSX in React 16; cast so it renders as a DOM attr.
  const inertAttr = (on: boolean) =>
    (on ? { inert: '' } : {}) as Record<string, unknown>

  const displaySlot = (
    <div
      className={cx(
        'slot',
        'display-slot',
        { 'is-visible': !editing },
        { 'affordance-border': hoverAffordance === 'border' && !disabled },
        { 'is-disabled': disabled },
        { 'has-disabled-tooltip': disabled && !!disabledTooltip },
        classNames?.display
      )}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={editing || disabled ? -1 : 0}
      role={disabled ? undefined : 'button'}
      onFocus={() => setEditing(true)}
      onClick={() => setEditing(true)}
      onKeyDown={handleDisplayKeyDown}
      aria-hidden={editing}
      {...inertAttr(editing)}
    >
      <span className={cx('display-content')}>{display}</span>
      {editIcon && !disabled && (
        <Icon
          name={editIcon}
          iconStyle='solid'
          className={cx('edit-icon')}
          aria-hidden
        />
      )}
    </div>
  )

  return (
    <div
      className={cx('inline-input', `sizing-${sizing}`, className)}
      style={style}
      data-elm-id={elmId}
    >
      {disabled && disabledTooltip ? (
        <PopupTooltip
          on='hover'
          position='top center'
          offset={0}
          horizontalOffset={undefined}
          verticalOffset={undefined}
          trigger={displaySlot}
          content={disabledTooltip}
        />
      ) : (
        displaySlot
      )}
      <div
        ref={editRef}
        className={cx(
          'slot',
          'edit-slot',
          { 'is-visible': editing },
          classNames?.edit
        )}
        onBlur={handleEditBlur}
        onKeyDown={handleEditKeyDown}
        aria-hidden={!editing}
        {...inertAttr(!editing)}
      >
        {edit}
      </div>
    </div>
  )
}

export default InlineInput
